import React, { useState, useEffect, useRef } from 'react';
import Splash from '../../../../splash/Splash';

function APIS({ setGlobalData, refreshPvtData }) {
    const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('token'));
    const [isLoadingPublic, setIsLoadingPublic] = useState(true);
    const abortControllerRef = useRef(null);

    // Define API data states
    const [apiData, setApiData] = useState({
        sliders: { main: [], favourite: [], feature: [] },
        socialLinks: [],
        general: {},
        paymentMethods: [],
        hasEWallet: false,
        promotions: { activePromotion: [], displayPromotion: [], allPromotions: [] },
        userData: null,
        transactions: [],
        turnovers: [],
        marketBlock: [],
    });

    // Fetch Public APIs (No login required) — with AbortController
    useEffect(() => {
        const ctrl = new AbortController();
        abortControllerRef.current = ctrl;

        const fetchPublicData = async () => {
            setIsLoadingPublic(true);
            try {
                const token = localStorage.getItem('token');
                const headers = token ? { Authorization: `Bearer ${token}` } : {};
                let hasEWallet = false;

                const apiResponses = await Promise.all([
                    fetch(`${process.env.REACT_APP_API_URL}/api/sliders`, { headers, signal: ctrl.signal }).then(res => res.json()),
                    fetch(`${process.env.REACT_APP_API_URL}/api/sociallink`, { headers, signal: ctrl.signal }).then(res => res.json()),
                    fetch(`${process.env.REACT_APP_API_URL}/api/general`, { headers, signal: ctrl.signal }).then(res => res.json()),
                    fetch(`${process.env.REACT_APP_API_URL}/api/payment-methods`, { headers, signal: ctrl.signal }).then(res => res.json()),
                    fetch(`${process.env.REACT_APP_API_URL}/api/promotion`, { headers, signal: ctrl.signal }).then(res => res.json()),
                ]);

                const [slidersData, socialData, generalData, paymentData, promotionsData] = apiResponses;
                let pgs = [];

                if (paymentData?.type === 'ewallet') {
                    pgs = paymentData?.pgs || [];
                    hasEWallet = true;
                } else {
                    pgs = Array.isArray(paymentData) ? paymentData : [];
                }

                setApiData(prevData => ({
                    ...prevData,
                    hasEWallet,
                    sliders: {
                        main: (slidersData || []).filter(slider => slider.status === 'active' && slider.slider_type === 'Main'),
                        favourite: (slidersData || []).filter(slider => slider.status === 'active' && slider.slider_type === 'Favourite'),
                        feature: (slidersData || []).filter(slider => slider.status === 'active' && slider.slider_type === 'Features'),
                    },
                    socialLinks: (Array.isArray(socialData) ? socialData[0] : socialData) || {},
                    general: (Array.isArray(generalData) ? generalData[0] : generalData) || {},
                    paymentMethods: pgs,
                    promotions: {
                        activePromotion: (promotionsData || []).filter(promo => promo.status === 'active'),
                        displayPromotion: (promotionsData || []).filter(promo => promo.status === 'display'),
                        allPromotions: promotionsData || [],
                    },
                }));
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error('Error fetching public data:', error);
                }
            } finally {
                setIsLoadingPublic(false);
            }
        };

        fetchPublicData();
        return () => ctrl.abort();
    }, []);

    // Fetch Private APIs (Login required) — with AbortController
    useEffect(() => {
        const token = localStorage.getItem('token');
        const remoteId = localStorage.getItem('remote_id');
        const ctrl = new AbortController();

        if (isLoggedIn && token && remoteId) {
            const fetchPrivateData = async () => {
                try {
                    const apiResponses = await Promise.all([
                        fetch(`${process.env.REACT_APP_API_URL}/api/user/show?remote_id=${remoteId}`, {
                            headers: { Authorization: `Bearer ${token}` },
                            signal: ctrl.signal,
                        }).then(res => res.json()),
                        fetch(`${process.env.REACT_APP_API_URL}/api/transaction/show?remote_id=${remoteId}`, {
                            headers: { Authorization: `Bearer ${token}` },
                            signal: ctrl.signal,
                        }).then(res => res.json()),
                        fetch(`${process.env.REACT_APP_API_URL}/api/turnovers?remote_id=${remoteId}`, {
                            headers: { Authorization: `Bearer ${token}` },
                            signal: ctrl.signal,
                        }).then(res => res.json()),
                        fetch(`${process.env.REACT_APP_API_URL}/api/market/block`, {
                            headers: { Authorization: `Bearer ${token}` },
                            signal: ctrl.signal,
                        }).then(res => res.json()),
                    ]);

                    const [userData, transactions, turnovers, marketBlock] = apiResponses;

                    setApiData(prevData => ({
                        ...prevData,
                        userData: Array.isArray(userData) ? userData[0] : userData,
                        transactions: Array.isArray(transactions) ? transactions : [],
                        turnovers: turnovers?.turnover || [],
                        marketBlock: Array.isArray(marketBlock) ? marketBlock : [],
                    }));
                } catch (error) {
                    if (error.name !== 'AbortError') {
                        console.error('Error fetching private data:', error);
                    }
                }
            };

            fetchPrivateData();
        }

        return () => ctrl.abort();
    }, [isLoggedIn, refreshPvtData]);

    // Pass API data to global state in App.js (debounced to prevent cascading updates)
    useEffect(() => {
        setGlobalData(apiData);
    }, [apiData, setGlobalData]);

    if (isLoadingPublic) {
        return <Splash />;
    }

    return null;
}

export default APIS;
